import React, { useEffect, useMemo, useRef, useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getGraph, GraphData, saveBlob } from "./adminApi";
import CsvMetadata from "./CsvMetadata";
import { colorFor, initialVisibility, loadSaved, saveChoice } from "./graphSeries";
import SeriesToggles, { ThresholdLine } from "./SeriesToggles";

/** Draw the chart's SVG onto a white canvas and download it as a PNG. */
async function svgToPng(svg: SVGSVGElement, filename: string): Promise<void> {
  const { width, height } = svg.getBoundingClientRect();
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Could not draw the graph"));
      img.src = url;
    });
    const scale = 2; // sharper image
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not draw the graph");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!blob) throw new Error("Could not save the graph");
    saveBlob(blob, filename);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Sensor graph of one CSV file, with the activity's threshold lines and "Save as PNG". */
export default function CsvGraph({ s3Key, artifactId }: { s3Key: string; artifactId: string }) {
  const [data, setData] = useState<GraphData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const [thresholdOn, setThresholdOn] = useState<Record<string, boolean>>(() => loadSaved().thresholds);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    getGraph(s3Key)
      .then((g) => {
        if (!live) return;
        setData(g);
        setVisible(initialVisibility(g.series.map((s) => s.name), loadSaved().series));
      })
      .catch((e) => live && setError((e as Error).message));
    return () => {
      live = false;
    };
  }, [s3Key]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.xValues.map((x, i) => {
      const row: Record<string, number | null> = { x };
      data.series.forEach((s) => (row[s.name] = s.values[i] ?? null));
      return row;
    });
  }, [data]);

  const names = useMemo(() => (data ? data.series.map((s) => s.name) : []), [data]);

  const thresholds: ThresholdLine[] = (data?.thresholdOverlay?.descriptors || [])
    .filter((d) => typeof d.value === "number" && (!d.series || names.includes(d.series)))
    .map((d, i) => ({ id: d.id || d.label || `threshold-${i}`, label: d.label || d.id || "threshold", value: d.value as number, series: d.series, note: d.note }));
  // A threshold line is drawn only while it is switched on and its series (if any) is visible.
  const drawnThresholds = thresholds.filter((t) => thresholdOn[t.id] !== false && (!t.series || visible[t.series]));

  const changeSeries = (changes: Record<string, boolean>) => {
    setVisible((v) => ({ ...v, ...changes }));
    saveChoice("series", changes);
  };
  const changeThreshold = (id: string, on: boolean) => {
    setThresholdOn((t) => ({ ...t, [id]: on }));
    saveChoice("thresholds", { [id]: on });
  };

  const save = async () => {
    const svg = box.current?.querySelector("svg.recharts-surface") as SVGSVGElement | null;
    if (!svg) return;
    try {
      await svgToPng(svg, `${artifactId}.png`);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (error) return <p className="ui-banner ui-banner--error">{error}</p>;
  if (!data)
    return (
      <p>
        <span className="ui-spinner" aria-hidden="true" /> Loading graph…
      </p>
    );
  if (data.parseError || rows.length === 0) {
    return (
      <div>
        <CsvMetadata metadata={data.metadata} />
        <p>Could not graph this file{data.parseError ? `: ${data.parseError}` : ""}.</p>
      </div>
    );
  }

  return (
    <div>
      <CsvMetadata metadata={data.metadata} />
      <SeriesToggles
        names={names}
        visible={visible}
        onSeries={changeSeries}
        thresholds={thresholds}
        thresholdOn={thresholdOn}
        onThreshold={changeThreshold}
      />
      {!names.some((n) => visible[n]) && <p>All lines are switched off. Turn one on above.</p>}
      <div ref={box} style={{ width: "100%", height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="x" type="number" domain={["dataMin", "dataMax"]} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {names
              .filter((n) => visible[n])
              .map((n) => (
                <Line key={n} type="monotone" dataKey={n} stroke={colorFor(n, names)} dot={false} isAnimationActive={false} connectNulls />
              ))}
            {drawnThresholds.map((t) => (
              <ReferenceLine
                key={t.id}
                y={t.value}
                stroke={t.series ? colorFor(t.series, names) : "#6b7280"}
                strokeDasharray="6 4"
                label={{ value: `${t.label}${t.series ? ` (${t.series})` : ""}`, fontSize: 10, position: "insideTopRight" }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <button type="button" className="ui-btn ui-btn--sm ui-btn--secondary" onClick={save}>
        Save as PNG
      </button>
    </div>
  );
}
